import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import {Form} from "semantic-ui-react";
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { useMessageResources, useTextResources } from '../../../language';
import { useCallback } from "react";
import useBoolean from "../../../hooks/useBoolean";
import TextInput from "../../../components/shared/controlled/TextInput";
import CheckBox from "../../../components/shared/controlled/Checkbox";
import CustomersInGroupTransfer from "../components/CustomersInGroupTransfer";
import { useCurrentHotel } from "../../../hooks/useCurrentHotel";
import { onWarning } from '../../../components/shared/messages';

const customerGroupNameMaxLength = 200;

const defaultState = {
    Id: 0,
    Name: '',
    IsActive: true,
    CustomersInGroups: [],
};

const EditCustomerGroupPopup = ({
    open,
    customerGroup,
    save,
    onClose,
}) => {
    const text = useTextResources();
    const message = useMessageResources();
    const currentHotel = useCurrentHotel();
    const isCreate = !customerGroup;
    const {
        value: isLoading,
        setTrue: setLoading,
        setFalse: resetLoading
    } = useBoolean(false);
    const [customerGroupState, setCustomerGroupState] = useState(defaultState);
    const hotelChainId = currentHotel?.HotelChainId;

    useEffect(() => {
        setCustomerGroupState({
            ...defaultState,
            ...customerGroup,
            HotelChainId: hotelChainId
        });
    }, [open, customerGroup, hotelChainId]);

    const setName = useCallback((value) => {
        setCustomerGroupState((prevState) => ({
            ...prevState,
            Name: value,
        }));
    }, []);

    const setIsActive = useCallback((value) => {
        setCustomerGroupState((prevState) => ({
            ...prevState,
            IsActive: value,
        }));
    }, []);

    const setCustomersInGroups = useCallback((value) => {
        setCustomerGroupState((prevState) => ({
            ...prevState,
            CustomersInGroups: value,
        }));
    }, []);

    const isValid = useCallback((customerGroupState) => {
        if (!customerGroupState.Name) {
            onWarning(message("AllFieldsAreRequired"));
            return false;
        }

        return true;
    }, [message]);
    
    const handleSave = useCallback(async () => {
        if (!isValid(customerGroupState)) {
            return;
        }

        try {
            setLoading();
            await save(customerGroupState);
            if (isCreate) {
                setCustomerGroupState({
                    ...defaultState,
                    HotelChainId: hotelChainId,
                });
            }
        } catch {
            return false;
        } finally {
            resetLoading();
        }

        return true;
    }, [customerGroupState, hotelChainId, isCreate, isValid, resetLoading, save, setLoading]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose])

    const handleSaveAndClose = useCallback(async () => {
        const isSaved = await handleSave();
        isSaved && handleClose();
    }, [handleSave, handleClose]);

    return (
        <EditEntityPopup
            open={open}
            isCreate={isCreate}
            header={`${isCreate ? text("Create") : text("Edit")} ${text("CustomerGroup")}`}
            onClose={handleClose}
            onSave={isCreate ? handleSave : undefined}
            onSaveAndClose={handleSaveAndClose}
            isLoading={isLoading}
        >
            <Form>
                <Form.Field
                    label={text("Name")} 
                    control={TextInput}
                    onBlur={setName}
                    value={customerGroupState.Name}
                    placeholder={text("Name")}
                    maxLength={customerGroupNameMaxLength}
                />
                {!isCreate && (
                <Form.Field
                    label={text("Active")}
                    control={CheckBox}
                    onChange={setIsActive}
                    value={customerGroupState.IsActive}
                />
                )}
                <Form.Field
                    label={text("Customers")}
                    control={CustomersInGroupTransfer}
                    customerGroupId={customerGroupState.Id}
                    onChange={setCustomersInGroups}
                    value={customerGroupState.CustomersInGroups}
                />
            </Form>
        </EditEntityPopup>
    );
};

EditCustomerGroupPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    customerGroup: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
        IsActive: PropTypes.bool.isRequired,
        CustomersInGroups: PropTypes.arrayOf(PropTypes.shape({
            CustomerId: PropTypes.number.isRequired,
            CustomerGroupId: PropTypes.number.isRequired,
        })),
    }),
    onClose: PropTypes.func.isRequired,
    save: PropTypes.func,
};

EditCustomerGroupPopup.defaultProps = {
    open: false,
    customerGroup: undefined,
    onClose: () => {},
    save: () => Promise.resolve(),
}

export default EditCustomerGroupPopup;